/**
 * Where this build is running, and how it talks to the API.
 *
 * The same React bundle ships three ways:
 *
 *   1. **the website** — served from vedaunion.org, same-origin API, session
 *      cookie. `API_ORIGIN` is '' and every request stays relative.
 *   2. **an installed PWA** — same as (1); Safari/Chrome keep the cookie. The
 *      only difference is `display: standalone`, which we detect for chrome.
 *   3. **the packaged app** (Capacitor) — the bundle is loaded from a LOCAL
 *      origin inside the WebView, so `/api/...` would resolve to the WebView
 *      itself. `API_ORIGIN` is baked in at build time (VITE_API_ORIGIN) and the
 *      session travels as an `Authorization: Bearer` header instead of a cookie,
 *      because a cross-site cookie to vedaunion.org would be dropped.
 *
 * Nothing here does I/O at import time — `initPlatform()` is awaited once in
 * main.tsx before the first render, so `authToken()` is warm by the time any
 * request goes out.
 */

/** True when running inside the Capacitor native shell (Android / iOS app). */
export const isNativeApp: boolean =
  typeof window !== 'undefined' &&
  // Capacitor injects this global; `isNativePlatform()` is false in a browser
  // even when the JS bundle happens to include the plugins.
  !!(window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

/**
 * True when running inside the Tauri desktop shell (Windows / macOS / Linux).
 *
 * Tauri injects `__TAURI_INTERNALS__` before any of our code runs. The check is
 * a global, not a build flag, so ONE bundle behaves correctly whether it is
 * opened in a browser or loaded by the shell — the same property that lets the
 * web build and the Capacitor build share a file.
 */
export const isDesktopApp: boolean =
  typeof window !== 'undefined'
  && (window as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ !== undefined;

/** True when launched from a home-screen install (iOS/Android PWA), not a tab. */
export const isStandalonePwa: boolean =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari predates the media query and still uses this non-standard flag.
    (navigator as { standalone?: boolean }).standalone === true);

/** True for a packaged app, the desktop shell or an installed PWA — i.e. "no
 *  browser chrome". */
export const isAppShell: boolean = isNativeApp || isDesktopApp || isStandalonePwa;

/**
 * True when the bundle is served from a LOCAL origin rather than the site's.
 *
 * The Capacitor shell (`https://localhost` / `capacitor://localhost`) and the
 * Tauri shell (`http://tauri.localhost` on Windows, `tauri://localhost`
 * elsewhere) share the whole consequence of that: `/api/...` would resolve to
 * the WebView itself, so every path has to be made absolute against
 * `API_ORIGIN`, and the session travels as a bearer header because a cross-site
 * cookie to vedaunion.org would be dropped.
 *
 * One name for one fact, so a new shell only has to be added here.
 */
export const isLocalOrigin: boolean = isNativeApp || isDesktopApp;

/**
 * Absolute origin of the API, or '' when it is same-origin.
 *
 * Set VITE_API_ORIGIN only for the packaged-app build (see .env.app). A trailing
 * slash is trimmed so `API_ORIGIN + '/api/me'` never doubles up.
 */
export const API_ORIGIN: string = (import.meta.env.VITE_API_ORIGIN ?? '').replace(/\/+$/, '');

/**
 * Resolve an app-relative path against the API origin.
 *
 * Absolute URLs pass through untouched — media already comes back from the API
 * as `https://media.vedaunion.org/...`, and we must not rewrite those.
 */
export function resolveUrl(path: string): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('//')) return path;
  if (!API_ORIGIN) return path;
  return path.startsWith('/') ? API_ORIGIN + path : `${API_ORIGIN}/${path}`;
}

// -- session token (packaged app only) ---------------------------------------

const TOKEN_KEY = 'vu:session-token';

let token: string | null = null;
let store: {
  get: (k: string) => Promise<string | null>;
  set: (k: string, v: string) => Promise<void>;
  remove: (k: string) => Promise<void>;
} | null = null;

/**
 * Load the persisted session token before the first render.
 *
 * Uses Capacitor Preferences (SharedPreferences on Android, UserDefaults on iOS)
 * rather than localStorage: it survives a WebView data clear and an app update,
 * which is what makes "you never log in again" actually hold. Sessions carry a
 * 30-day sliding expiry server-side, so opening the app once a month is enough
 * to stay signed in indefinitely.
 *
 * No-ops on the website, where the HttpOnly cookie is the session and JS should
 * never hold a token at all.
 */
export async function initPlatform(): Promise<void> {
  if (isDesktopApp) {
    // The desktop shell has no Capacitor. `localStorage` inside the Tauri
    // WebView is per-app and persists across restarts and updates, which is the
    // property that matters; it is NOT the browser's localStorage, because the
    // shell's origin is its own.
    store = {
      get: async (k) => { try { return window.localStorage.getItem(k); } catch { return null; } },
      set: async (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* private mode */ } },
      remove: async (k) => { try { window.localStorage.removeItem(k); } catch { /* ignore */ } },
    };
    token = await store.get(TOKEN_KEY);
    return;
  }
  if (!isNativeApp) return;
  const { Preferences } = await import('@capacitor/preferences');
  store = {
    get: async (k) => (await Preferences.get({ key: k })).value ?? null,
    set: async (k, v) => Preferences.set({ key: k, value: v }),
    remove: async (k) => Preferences.remove({ key: k }),
  };
  token = await store.get(TOKEN_KEY);
}

/** The current bearer token, or null (always null on the website). */
export function authToken(): string | null {
  return token;
}

/** Persist the token handed back by login/register. */
export function setAuthToken(next: string | null): void {
  token = next;
  if (!store) return;
  void (next ? store.set(TOKEN_KEY, next) : store.remove(TOKEN_KEY));
}
